import './template-styles/ArticlePage.scss'

import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { dynamicGa4DataLayerPush, isProductHub } from '@which/shared'

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react'

import type { Author, ImageWithSources, ReviewsAdviceSingleQuery } from '../../generated/frontend'
import { useReviewsAdviceSingleQuery } from '../../generated/frontend'
import type { PageParams } from '../../routes'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import {
  AffiliateDisclaimer,
  AssociatedArticles,
  BackToTop,
  Breadcrumb,
  ErrorComponent,
  HideOnArticleScroll,
  Loader,
  LocalFooter,
  PageError,
  PromoBlock,
  Redirect,
  ResubscribeBanner,
  SharingOptions,
  ToolCTAWithHeroImage,
} from '../../shared/components'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { GridArticle, GridItemArticle } from '../../shared/grids'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import { articleBodyRenderer } from '../../shared/renderers/article-body-renderer'
import type { PageInfo } from '../../shared/types/PageInfo'
import { usePageProps } from '../../shared/usePageProps'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import { ArticleHero } from '../article/components/ArticleHero/ArticleHero'
import { AuthorWrapper } from '../article/components/Author/AuthorWrapper'
import { Header } from '../article/components/Header/Header'
import { IntroParagraph } from '../article/components/IntroParagraph/IntroParagraph'
import { WhichConnectMessage } from '../article/components/WhichConnectMessage/WhichConnectMessage'
import { ToastNotification } from '../reviews/shared/components/ToastNotification'
import { ArticleTemplateContext } from './ArticleTemplateContext'
import { ArticleMemberBenefits } from './components/ArticleMemberBenefits'
import { CompareTrayWrapper } from './components/CompareTrayWrapper/CompareTrayWrapper'
import { ArticlePageBodyGam } from './components/Gam/ArticlePageBodyGam'
import { ArticlePageRhsGam } from './components/Gam/ArticlePageRhsGam'
import { JumpLinks } from './components/JumpLinks/JumpLinks'
import {
  RecommendedForYouArticles,
  RecommendedForYouPageBody,
} from './components/RecommendedForYouArticles'
import type { SquirrelScriptsData } from './hooks/use-squirrel-script'
import { useSquirrelScript } from './hooks/use-squirrel-script'
import { UserFeedbackIframe } from './page-snippets/user-feedback-iframe'
import { baseDataLayer } from './utils/base-data-layer'
import { dynamicTableDatalayer } from './utils/dynamic-table-datalayer'
import { addTrackingCode, isArticleInTest } from './utils/is-article-in-test'
import { renderResubscribeBanner } from './utils/render-resubscribe-banner'
import { generateSchema } from './utils/schema'

const ReviewsAdviceSinglePage: React.FC = () => {
  const pageParams = useParams<PageParams>()
  const { pathname, search } = useLocation()
  const { context = '' } = usePageProps()

  const { loading, error, data } = useReviewsAdviceSingleQuery({
    variables: { url: `${pathname}${search}`, context },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })

  const { getBaseMetaTags } = useBaseMetaTags()

  useSquirrelScript(data as SquirrelScriptsData)

  const mortgageCtaTest = useFeatureIsOn('whc-992-mortgage-cta')
  const resubscribeBannerFlag = useFeatureIsOn('resubscribe-banner')
  const gbTestValue = useFeatureValue('ctx-011-contextualised-newsletter', '')
  const productHubSidebarTest = useFeatureValue('whc-812-hub-sidebar', null)

  const { userAccessState } = usePageProps()
  const transformTypeDecision = userAccessState?.transformTypeDecision ?? ''
  const isPaidMember = ['AUTHENTICATED_FULL_ACCESS', 'FULL_ACCESS'].includes(transformTypeDecision)

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.articlePage) {
    return <PageError pageName="Article Page" />
  }

  const { articlePage } = data

  if (articlePage.__typename === 'Redirect') {
    return <Redirect to={articlePage.redirectUrl} />
  }

  const {
    articleBodyTemplate,
    articleConfig,
    authors,
    breadcrumb,
    header,
    heroImage,
    introParagraph,
    jumpLinks,
    meta,
    moreOnArticles,
    optionalArticleData,
    relatedArticles,
    sharingData,
    showResubBanner,
    heroCTA: heroCTAData,
  } = articlePage

  const articleInTest = isArticleInTest(pageParams)
  const showVariant = articleInTest && mortgageCtaTest
  const heroCTA = {
    ...heroCTAData,
    button_url: addTrackingCode(heroCTAData?.button_url, articleInTest, showVariant),
  }
  const showHeroCta = showVariant || pageParams?.subVerticalSlug !== 'mortgages-and-property'
  const baseMetaTags = getBaseMetaTags({
    meta,
    context,
    imageUrl: heroImage?.src,
    twitterCard: 'summary_large_image',
  })
  const metaTags = getMetaTags({
    articlePage,
    baseMetaTags,
    urls: { currentUrl: getFullUrl(), previousUrl },
    gbTestValue,
  })

  const gamPageInfo = {
    vertical: meta.taxonomyHierarchy.vertical?.slug,
    sub_vertical: meta.taxonomyHierarchy.subVertical?.slug,
    category: meta.taxonomyHierarchy.category?.slug,
    pageType: 'article',
    tags: meta.keywords,
    content_id: meta.contentId,
  }

  const moneyPromoBlockData = optionalArticleData?.components?.moneyPromoBlock
  const moneyPromoBlock = {
    ...moneyPromoBlockData,
    ctaLink: addTrackingCode(moneyPromoBlockData?.ctaLink, articleInTest, showVariant),
  }
  const consumerRightsAndMoneyLocalFooter = optionalArticleData?.components?.localFooter

  const promoBlockTracking = (position: string) => {
    return {
      onClick: () => {
        dynamicGa4DataLayerPush({
          event: 'click_cta',
          utagid: 'WHC873DP01',
          item_group: 'in article cta',
          item_text: moneyPromoBlock?.ctaLabel,
          item_parent_text: moneyPromoBlock?.title,
          item_spec: position,
        })
      },
    }
  }

  const handleShowMoreOnJumplinkClick = () => {
    const showMoreBtn = document.querySelector<HTMLButtonElement>(
      '[data-testid="show-more-button"]'
    )

    if (showMoreBtn) {
      showMoreBtn.click()
    }
  }

  // TODO: We might want to do some of this logic in data-provider if implementing properly
  // Added for AB test WHC-812
  let moreOnThisAdditionalProps = {}

  if (meta && meta.taxonomyHierarchy) {
    const { category } = meta.taxonomyHierarchy
    const productHubCategory = isProductHub(category)

    if (productHubCategory) {
      const productHubLink = `/l/${meta.taxonomyHierarchy.subVertical.slug}/${category.slug}`

      if (productHubSidebarTest === 'Variation 1') {
        moreOnThisAdditionalProps = {
          title: '',
          titleCategory: category.name.toLowerCase(),
          titleCategoryLink: productHubLink,
        }
      } else if (productHubSidebarTest === 'Variation 2') {
        moreOnThisAdditionalProps = {
          title: `More on ${category.name.toLowerCase()}`,
          titleCategory: category.name.toLowerCase(),
          titleCategoryLink: productHubLink,
          seeAllLink: true,
        }
      }
    }
  }

  const showToolCTAWithHero = Boolean(
    heroCTA && heroCTA.title && heroCTA.button_url && heroCTA.button_text
  )

  return (
    <ArticleTemplateContext.Provider
      value={{
        meta,
      }}
    >
      <div
        ref={domUpdates}
        data-testid="template-portal"
        data-template-type="reviews-advice-single-article"
      >
        {renderResubscribeBanner(resubscribeBannerFlag, transformTypeDecision) && (
          <ResubscribeBanner showBanner={showResubBanner} />
        )}
        <Breadcrumb {...breadcrumb} />
        <PageWrapperWithMeta metaTags={metaTags}>
          <GridArticle>
            <GridItemArticle area="header">
              <AffiliateDisclaimer affiliateDisclaimer={articleConfig?.affiliateDisclaimer} />
              <Header {...header} />
            </GridItemArticle>
            <GridItemArticle area="author">
              <AuthorWrapper authors={authors} />
            </GridItemArticle>
            <GridItemArticle area="hero">
              <>
                <ArticleHero
                  heroImage={heroImage as ImageWithSources}
                  compact={showToolCTAWithHero}
                />
                {showHeroCta && <ToolCTAWithHeroImage {...heroCTA} />}
              </>
            </GridItemArticle>
            <GridItemArticle area="jumplinks">
              <JumpLinks handleOnClick={handleShowMoreOnJumplinkClick} links={jumpLinks} />
            </GridItemArticle>
            <GridItemArticle area="share" className="gridarea-share">
              <HideOnArticleScroll>
                <SharingOptions sharingData={sharingData} />
              </HideOnArticleScroll>
            </GridItemArticle>
            <GridItemArticle area="body" className="gridarea-body">
              <IntroParagraph text={introParagraph} />
              {articleBodyRenderer(articleBodyTemplate, '', getFullUrl())}
              <WhichConnectMessage showMessage={articleConfig?.whichConnectMessage} />
              {moneyPromoBlock && !showVariant && (
                <PromoBlock {...moneyPromoBlock} {...promoBlockTracking('bottom of page')} />
              )}
              <div className="related-footer">
                <AssociatedArticles
                  componentType="more-on-this"
                  position="bottom"
                  className="associated-articles"
                  {...moreOnArticles}
                  {...moreOnThisAdditionalProps}
                />
                <div className="associated-articles">
                  <RecommendedForYouPageBody
                    componentType="related-articles"
                    position="bottom"
                    {...relatedArticles}
                  />
                </div>
              </div>
              <UserFeedbackIframe />
              <ArticlePageBodyGam pageInfo={gamPageInfo} />
              <SharingOptions sharingData={sharingData} orientation="vertical" />
              <div className="back-to-top-wrapper">
                <BackToTop />
              </div>
            </GridItemArticle>
            <GridItemArticle area="memberbenefits">
              <ArticleMemberBenefits />
            </GridItemArticle>

            <GridItemArticle area="related" className="gridarea-related">
              <aside className="sidebar">
                {moneyPromoBlock && !showVariant && (
                  <PromoBlock {...moneyPromoBlock} {...promoBlockTracking('sidebar')} />
                )}
                <AssociatedArticles
                  componentType="more-on-this"
                  position="right"
                  {...moreOnArticles}
                  {...moreOnThisAdditionalProps}
                />
                <RecommendedForYouArticles position="right" />
                <ArticlePageRhsGam pageInfo={gamPageInfo} />
              </aside>
            </GridItemArticle>
            {consumerRightsAndMoneyLocalFooter && (
              <GridItemArticle area="footer">
                <LocalFooter data={consumerRightsAndMoneyLocalFooter} />
              </GridItemArticle>
            )}
          </GridArticle>
        </PageWrapperWithMeta>
      </div>
      {isPaidMember && (
        <CompareTrayWrapper taxonomySlug={meta?.taxonomyHierarchy?.category?.slug} />
      )}
      <ToastNotification />
    </ArticleTemplateContext.Provider>
  )
}

export default ReviewsAdviceSinglePage

const domUpdates = () => {
  dynamicTableDatalayer()
}

export type ReviewsAdviceSingleArticleData = ReviewsAdviceSingleQuery['articlePage'] & {
  __typename: 'ArticlePage'
}

const getMetaTags = ({
  articlePage: { meta, articleBodyTemplate, heroImage, authors, articleConfig },
  baseMetaTags,
  urls,
  gbTestValue,
}: {
  articlePage: ReviewsAdviceSingleArticleData
  baseMetaTags: JSX.Element[]
  urls: { currentUrl: string; previousUrl?: string }
  gbTestValue: string
}) => {
  const authorsMeta = authors?.length
    ? [<meta key="author" name="author" content={authors.map(({ name }) => name).join(', ')} />]
    : []

  const keywords = meta.keywords?.length
    ? [<meta key="keywords" name="keywords" content={meta.keywords.join(', ')} />]
    : []

  const basePageInfo: PageInfo = {
    pageType: 'article',
    vertical: meta.taxonomyHierarchy?.vertical.slug,
    sub_vertical: meta.taxonomyHierarchy?.subVertical?.slug,
    super_category: meta.taxonomyHierarchy?.superCategory?.slug,
    category: meta.taxonomyHierarchy?.category?.slug,
    content_id: meta.contentId,
    isVideoArticle: false,
  }

  // data layer
  const dataLayerPageInfo: PageInfo = {
    ...basePageInfo,
    ...baseDataLayer(meta, urls, articleBodyTemplate),
    keywords: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    tags: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    author: authors?.length ? authors.map(({ name }) => name).join(' | ') : '',
    adviceType: articleConfig.adviceType,
    articleType: articleConfig.articleType,
    content_type: 'advice',
  }
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(dataLayerPageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, {
    ...basePageInfo,
    keywords: meta.keywords,
    tags: meta.keywords,
    GB_test_value: gbTestValue,
  })

  // schema for SEO
  const schema = generateSchema({
    meta,
    authors: (authors || []) as Author[],
    imageList: heroImage?.renditions,
  })

  return baseMetaTags.concat(
    authorsMeta,
    keywords,
    dataLayer,
    dfpMeta,
    schema,
    <link rel="preconnect" href="https://securepubads.g.doubleclick.net" />
  )
}
